import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SelectField } from "@/shared/ui/core";
import { DateField, FormRow, MoneyField, NumberField } from "@/features/admin/components";
import { useDiscountTypeOptions } from "../productOptions";

/**
 * Lo que pide una PROMOCIÓN: cuánto se descuenta y entre qué fechas.
 *
 * ── Los tres campos ya existían. No se duplican, se reutilizan ──────
 * `discount` y `validity` son grupos del contrato desde antes de esta
 * pantalla, y viven además en sus propios pasos —Precios y Vigencia—
 * donde un producto normal los edita. Aquí se pintan los MISMOS nombres
 * de campo (`discount.value`, `validity.from`, `validity.to`), no unos
 * gemelos: en react-hook-form dos controles con el mismo `name` son el
 * mismo dato, así que lo que se escriba aquí es lo que se ve allá y al
 * revés.
 *
 * Añadir `promoDiscount` y `promoFrom` habría sido más rápido y habría
 * dejado dos fuentes para lo mismo: un producto con un 20 % en el paso 2
 * y un 35 % en el paso 3, y la tienda eligiendo una de las dos.
 *
 * ── Solo `from` y `to`, no `durationDays` ───────────────────────────
 * La duración es otra idea —cuánto vale lo comprado una vez comprado,
 * los días de una membresía— y no tiene nada que ver con la ventana de
 * una promoción. Se queda en el paso de Vigencia, que es donde se
 * explica.
 *
 * La regla de que el fin no vaya antes del inicio la comprueba
 * `productFormSchema`, y el servidor la comprueba otra vez con
 * `validityWindowError`: es la misma función del contrato en los dos
 * lados, así que no pueden discrepar.
 *
 * ── Por qué NO es una tarjeta propia ────────────────────────────────
 * Lo fue, y medía 208 px con su marco, su título y su explicación. Eso
 * dejaba el paso en 1026 px a 1440×900, o sea 126 fuera de la pantalla,
 * y el objetivo permanente de este asistente es que cada paso quepa en
 * una vista.
 *
 * Metida DENTRO del bloque de clasificación cuesta 110: la fila de
 * campos y su renglón de título. Y además se lee mejor de lo que se leía
 * aparte, porque es cierto: una promoción se DEFINE por su descuento y
 * su ventana, igual que un producto se define por su categoría. No es
 * una decisión distinta puesta al lado, es parte de "qué es esto".
 */
export function ProductPromotionFields() {
  const { t } = useTranslation();
  const typeOptions = useDiscountTypeOptions();
  const type = (useWatch({ name: "discount.type" }) ?? "percent") as string;

  return (
    <FormRow className="gap-2">
      {/*
        El renglón de título. No es un `<legend>` —el bloque ya tiene el
        suyo, "Clasificación"— sino el rótulo de un grupo dentro de él,
        con el mismo tratamiento que usan las etiquetas del panel.
      */}
      <p className="text-[11px] font-semibold uppercase tracking-ds-label-wide text-ds-ink-3">
        {t("Promoción · descuento y vigencia")}
      </p>

      {/*
        DOS columnas, no cuatro.
        
        Se probaron cuatro para ahorrar una fila, y en pantalla fue peor:
        este grupo vive dentro del bloque de clasificación, que ocupa
        media pantalla (525 px), así que cuatro columnas son 120 px por
        campo. Medido en el navegador: el desplegable enseñaba "Porcent"
        cortado y los dos campos de fecha —que traen su propio icono de
        calendario y no se encogen— se salían de la tarjeta.
        
        A dos columnas cada campo tiene ~240 px, que es lo que un
        selector de fecha necesita, y la fila de más cabe: el paso queda
        en 871 de los 900 que hay.
      */}
      <FormRow className="sm:grid-cols-2">
        <SelectField name="discount.type" label={t("Tipo de descuento")} options={typeOptions} />

        {type === "fixed" ? (
          <MoneyField
            name="discount.value"
            label={t("Descuento")}
            hint={t("En dinero, como se lee: 150.00")}
          />
        ) : (
          <NumberField
            name="discount.value"
            label={t("Descuento")}
            inputMode="decimal"
            placeholder="15"
            hint={t("En porcentaje, de 0 a 100.")}
          />
        )}

        <DateField name="validity.from" label={t("Inicio")} />
        <DateField
          name="validity.to"
          label={t("Fin")}
          hint={t("No puede ir antes del inicio.")}
        />
      </FormRow>
    </FormRow>
  );
}
